import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { NumberInput, PercentInput } from "./StandardInput";
import { ContentCard } from "./StandardLayout";
import { colors } from "../theme";
import { formatPSF } from "../utils/formatters";
import { resolveBuildableSqFt } from "../utils/projectCostCalcs";

import { ConstructionLoader } from './ConstructionLoader';
// ── Static sub-components (defined OUTSIDE the main component to preserve
//    stable React identity across re-renders and prevent focus loss) ──

const rowShellSx = {
  display: "flex",
  alignItems: { xs: "flex-start", sm: "center" },
  flexDirection: { xs: "column", sm: "row" },
  justifyContent: "space-between",
  gap: { xs: 0.5, sm: 2 },
  py: 1,
} as const;

const listSx = {
  "& > *:not(:last-child)": {
    borderBottom: `1px solid ${colors.grey[300]}`,
  }
} as const;

const SectionHeader = ({ title, description }: { title: string; description: string }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "flex-start", sm: "baseline" },
      gap: { xs: 0.5, sm: 2 },
      mb: 1.5,
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.grey[900] }}>
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: colors.grey[600],
        lineHeight: 1.4,
        maxWidth: 520,
      }}
    >
      {description}
    </Typography>
  </Box>
);

const InputRow = ({ label, control }: { label: string; control: React.ReactNode }) => (
  <Box sx={rowShellSx}>
    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[700] }}>
      {label}
    </Typography>
    <Box sx={{ minWidth: { sm: 160 }, width: { xs: "100%", sm: "auto" }, textAlign: { xs: "left", sm: "right" } }}>
      {control}
    </Box>
  </Box>
);

const ValueRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={rowShellSx}>
    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[700] }}>
      {label}
    </Typography>
    <Typography className="num" variant="body2" sx={{ fontWeight: 700, color: colors.grey[900] }}>
      {value}
    </Typography>
  </Box>
);

const MetricTile = ({ label, value, highlight = false }: { label: string; value: React.ReactNode; highlight?: boolean }) => (
  <Box
    sx={{
      border: `1px solid ${colors.grey[300]}`,
      borderRadius: 1,
      px: 1.5,
      py: 1,
      backgroundColor: highlight ? colors.blueTint : colors.grey[50],
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[700] }}>
      {label}
    </Typography>
    <Typography className="num" variant="body2" sx={{ fontWeight: 700, color: colors.grey[900] }}>
      {value}
    </Typography>
  </Box>
);

const LoadingOverlay = () => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.5,
      backgroundColor: "rgba(255, 255, 255, 0.68)",
      backdropFilter: "blur(2px)",
    }}
  >
    <ConstructionLoader size={32} label="Updating summary" />
    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[700] }}>
      Updating summary...
    </Typography>
  </Box>
);

export default function ExitAssumptions({
  modelDetails,
  handleFieldChange,
  showRetail = false,
  showRentalUnits = true,
  variables,
  numUnits = 0,
  developmentModel = false,
  finalMetricsCalculating
}: {
  modelDetails: any;
  handleFieldChange: (
    fieldId: string,
    field_key: string,
    value: string | number
  ) => void;
  showRetail?: boolean;
  showRentalUnits?: boolean;
  variables?: any;
  numUnits?: number;
  developmentModel?: boolean;
  finalMetricsCalculating?: boolean;
}) {

  
  // Exact field_key strings (must match backend)
  const K = {
    mfExitMonth: "Multifamily Exit Month",
    mfCapRate: "Multifamily Applied Exit Cap Rate",
    mfSellingCosts: "Multifamily Less: Selling Costs",
    rtExitMonth: "Retail Exit Month",
    rtCapRate: "Retail Applied Exit Cap Rate",
    rtSellingCosts: "Retail Less: Selling Costs",
  };



  const getFieldValue = (field_key: string, def: any) => {
    const f = modelDetails?.user_model_field_values?.find(
      (x: any) => x.field_key === field_key
    );
    const v = f?.value;
    return v === "" || v === undefined || v === null ? def : v;
  };

  const getFieldId = useCallback(
    (field_key: string) => {
      const f = modelDetails?.user_model_field_values?.find(
        (x: any) => x.field_key === field_key
      );
      return f ? f.field_id : "";
    },
    [modelDetails]
  );

  // Helpers for currency and numeric parsing
  const parseNumber = (val: any, fallback = 0): number => {
    if (val === undefined || val === null || val === "") return fallback;
    if (typeof val === "number") return val;
    const s = String(val).trim();
    const hasParens = /^\(.*\)$/.test(s);
    const cleaned = s.replace(/[(),]/g, "");
    const n = Number(cleaned.replace(/[^0-9.+-]/g, ""));
    if (!Number.isFinite(n)) return fallback;
    return hasParens ? -Math.abs(n) : n;
  };

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  const formatCurrencyParens = (n: number) =>
    n < 0 ? `(${formatCurrency(Math.abs(n))})` : formatCurrency(n);

  // Local state for editable fields
  const [mfExitMonth, setMfExitMonth] = useState<string>(String(getFieldValue(K.mfExitMonth, "60")));
  const [mfCapRate, setMfCapRate] = useState<string>(String(getFieldValue(K.mfCapRate, "6")));
  const [mfSelling, setMfSelling] = useState<string>(String(getFieldValue(K.mfSellingCosts, "3")));

  const [rtExitMonth, setRtExitMonth] = useState<string>(String(getFieldValue(K.rtExitMonth, "60")));
  const [rtCapRate, setRtCapRate] = useState<string>(String(getFieldValue(K.rtCapRate, "8")));
  const [rtSelling, setRtSelling] = useState<string>(String(getFieldValue(K.rtSellingCosts, "3")));

  // Track the last known model values so we only reset when they actually change
  const lastModelValuesRef = useRef<string>("");
  // Track whether the user is actively editing to prevent stale resets
  const editingRef = useRef(false);

  useEffect(() => {
    if (editingRef.current) return;

    const snapshot = JSON.stringify([
      getFieldValue(K.mfExitMonth, "60"),
      getFieldValue(K.mfCapRate, "6"),
      getFieldValue(K.mfSellingCosts, "3"),
      getFieldValue(K.rtExitMonth, "60"),
      getFieldValue(K.rtCapRate, "8"),
      getFieldValue(K.rtSellingCosts, "3"),
    ]);

    if (snapshot === lastModelValuesRef.current) return;
    lastModelValuesRef.current = snapshot;

    setMfExitMonth(String(getFieldValue(K.mfExitMonth, "60")));
    setMfCapRate(String(getFieldValue(K.mfCapRate, "6")));
    setMfSelling(String(getFieldValue(K.mfSellingCosts, "3")));
    setRtExitMonth(String(getFieldValue(K.rtExitMonth, "60")));
    setRtCapRate(String(getFieldValue(K.rtCapRate, "8")));
    setRtSelling(String(getFieldValue(K.rtSellingCosts, "3")));
  }, [modelDetails]);

  const handleFocus = () => { editingRef.current = true; };
  const handleEditBlur = (fieldKey: string, value: string) => {
    const v = value;
    const id = getFieldId(fieldKey);
    handleFieldChange(id, fieldKey, v);
    // Allow time for the committed value to propagate back through modelDetails
    setTimeout(() => { editingRef.current = false; }, 500);
  };

  const space_type =
    (modelDetails?.user_model_field_values || []).find((f: any) => {
      const k = String(f.field_key || '');
      return k === 'space_type' || k.trim() === 'space_type';
    })?.value ?? 'Retail';

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 1200, mx: "auto", width: "100%" }}>
      {showRentalUnits && (
        <ContentCard title="Exit Assumptions">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <SectionHeader title="Assumptions" description="Exit timing and pricing inputs." />
              <Box sx={listSx}>
                <InputRow
                  label="Exit Month"
                  control={
                    <NumberInput
                      value={mfExitMonth}
                      onFocus={handleFocus}
                      onChange={(e) => setMfExitMonth(e.target.value)}
                      onBlur={() => handleEditBlur(K.mfExitMonth, mfExitMonth)}
                      suffix="months"
                      allowDecimals={false}
                      noCommas
                      fullWidth
                      inputProps={{ min: 1, max: 130 }}
                    />
                  }
                />
                <InputRow
                  label="Applied Exit Cap Rate"
                  control={
                    <PercentInput
                      value={mfCapRate}
                      onFocus={handleFocus}
                      onChange={(e) => setMfCapRate(e.target.value)}
                      onBlur={() => handleEditBlur(K.mfCapRate, mfCapRate)}
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                  }
                />
                <InputRow
                  label="Less: Selling Costs"
                  control={
                    <PercentInput
                      value={mfSelling}
                      onFocus={handleFocus}
                      onChange={(e) => setMfSelling(e.target.value)}
                      onBlur={() => handleEditBlur(K.mfSellingCosts, mfSelling)}
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                  }
                />
              </Box>
            </Box>
            <Box sx={{ position: "relative", overflow: "hidden", borderRadius: 1 }}>
              {finalMetricsCalculating && <LoadingOverlay />}
              <SectionHeader title="Results" description="Implied valuation and proceeds." />
              <Box sx={listSx}>
                <ValueRow
                  label={`Forward NOI in ${variables?.["Deal Time Horizon"] ?? ""}`}
                  value={formatCurrency(parseNumber(variables?.["Forward NOI in Month"], 0))}
                />
                <ValueRow
                  label="Implied Valuation at Exit"
                  value={formatCurrency(parseNumber(variables?.["Implied Valuation at Exit"], 0))}
                />
                <ValueRow
                  label="Implied Valuation per Unit"
                  value={(() => {
                    const totalVal = parseNumber(variables?.["Implied Valuation at Exit"], 0);
                    const perUnit = numUnits > 0 ? Math.round(totalVal / numUnits) : 0;
                    return formatCurrency(perUnit);
                  })()}
                />
                <ValueRow
                  label="Selling Costs"
                  value={formatCurrencyParens(parseNumber(variables?.["Selling Costs"], 0))}
                />
                <ValueRow
                  label="Net Reversion Proceeds"
                  value={formatCurrency(parseNumber(variables?.["Net Reversion Proceeds"], 0))}
                />
              </Box>
            </Box>
          </Box>
        </ContentCard>
      )}

      {showRetail && !showRentalUnits && !developmentModel && (
        <ContentCard title="Exit Assumptions">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <SectionHeader title="Assumptions" description="Exit timing and pricing inputs." />
              <Box sx={listSx}>
                <InputRow
                  label="Exit Month"
                  control={
                    <NumberInput
                      value={rtExitMonth}
                      onFocus={handleFocus}
                      onChange={(e) => setRtExitMonth(e.target.value)}
                      onBlur={() => handleEditBlur(K.rtExitMonth, rtExitMonth)}
                      suffix="months"
                      allowDecimals={false}
                      noCommas
                      fullWidth
                      inputProps={{ min: 0, step: 1 }}
                    />
                  }
                />
                <InputRow
                  label="Applied Exit Cap Rate"
                  control={
                    <PercentInput
                      value={rtCapRate}
                      onFocus={handleFocus}
                      onChange={(e) => setRtCapRate(e.target.value)}
                      onBlur={() => handleEditBlur(K.rtCapRate, rtCapRate)}
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                  }
                />
                <InputRow
                  label="Less: Selling Costs"
                  control={
                    <PercentInput
                      value={rtSelling}
                      onFocus={handleFocus}
                      onChange={(e) => setRtSelling(e.target.value)}
                      onBlur={() => handleEditBlur(K.rtSellingCosts, rtSelling)}
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                  }
                />
              </Box>
            </Box>
            <Box sx={{ position: "relative", overflow: "hidden", borderRadius: 1 }}>
              {finalMetricsCalculating && <LoadingOverlay />}
              <SectionHeader title="Results" description="Implied valuation and proceeds." />
              <Box sx={listSx}>
                <ValueRow
                  label={`Forward NOI in ${variables?.["Deal Time Horizon"] ?? ""}`}
                  value={formatCurrency(parseNumber(variables?.["Retail: Forward NOI in Month"], 0))}
                />
                {!developmentModel && (
                  <ValueRow
                    label="Implied Valuation at Exit"
                    value={formatCurrency(parseNumber(variables?.["Retail: Implied Valuation at Exit"], 0))}
                  />
                )}
                {developmentModel && (
                  <ValueRow
                  label="Implied Valuation at Exit"
                  value={formatCurrency(parseNumber(variables?.["Implied Valuation at Exit"], 0))}
                />
                )}
                {/* {numUnits > 0 && !developmentModel && (
                  <ValueRow
                    label="Implied Valuation per Unit"
                    value={(() => {

                      const totalVal = parseNumber(variables?.["Retail: Implied Valuation at Exit"], 0);
                      const perUnit = numUnits > 0 ? Math.round(totalVal / numUnits) : 0;
                      return formatCurrency(perUnit);
                    })()}
                  />
                )} */}
                {/* {numUnits > 0 && developmentModel && (
                  <ValueRow
                    label="Implied Valuation per Unit"
                    value={(() => {

                      const totalVal = parseNumber(variables?.["Implied Valuation at Exit"], 0);
                      const perUnit = numUnits > 0 ? Math.round(totalVal / numUnits) : 0;
                      return formatCurrency(perUnit);
                    })()}
                  />
                )} */}
                
                {numUnits === 0 && (
                  <ValueRow
                    label="Implied Valuation per SF"
                    value={(() => {
                      if (developmentModel) {
                        // Resolved via the shared helper: a duplicated field_key used to
                        // render this as "(0 SF)".
                        const buildableSf = resolveBuildableSqFt(modelDetails);
                        return formatCurrency(parseNumber(variables?.["Implied Valuation at Exit"], 0)) + " / SF" + " (" + buildableSf.toLocaleString() + " SF)";
                      } else {
                        const totalVal = parseNumber(variables?.["Retail: Implied Valuation at Exit"], 0);
                        const SF = getFieldValue("Gross Square Feet", 0);
                        const perSF = totalVal / SF;
                        return formatPSF(perSF) + " / SF";
                      }
                    })()}
                  />
                )}
                <ValueRow
                  label="Selling Costs"
                  value={formatCurrencyParens(parseNumber(variables?.["Retail: Selling Costs"], 0))}
                />
                <ValueRow
                  label="Net Reversion Proceeds"
                  value={formatCurrency(parseNumber(variables?.["Retail: Net Reversion Proceeds"], 0))}
                />
              </Box>
            </Box>
          </Box>
        </ContentCard>
      )}

      {/* Combined Exit Summary removed per CEO directive (May 2026) — Mixed Use now uses
          a single cap rate applied to combined NOI; Matt is updating the Google Sheet
          formulas so "Forward NOI in Month" / "Implied Valuation at Exit" represent the
          combined property values for Mixed Use. */}
    </Box>
  );
}
